import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, IconButton, InputAdornment, InputBase, makeStyles, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import PencilIcon from '@material-ui/icons/Create';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import CancelIcon from '@material-ui/icons/Cancel';
import FolderIcon from '@material-ui/icons/Folder';
import clsx from "clsx";
import { createRef, forwardRef, memo, Suspense, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { alertPosition, useAlert } from "../../hooks/useAlert";
import { Loading } from "../../parts/Loading";
import { useCustomerExternalFileList } from "../../store/PointAreaSettingState";
import * as FPEUtils from "../../utils/FPEUtils";
import { DATA_HAS_BEEN_CHANGED_ERROR } from "../../utils/LinksAPI";
import { POINT_DEFAULT_ICONS } from "./PointAreaSettingIconList";
import RoleControl from "../RoleControl";
import { SUBJ_POINT_AREA_SETTING_FILE, VERB_CREATE, VERB_DELETE, VERB_UPDATE } from "../../utils/AccessControl";

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TOTAL_FILE_SIZE = 200 * 1024 * 1024;
const MAX_FILE_NUMBER = 100;
const NO_CHANGE_FILE_SENTINEL = '##FILE##';

const useStyles = makeStyles({
	container: {
		height: '100%',
		// border: '1px solid #a7a7a7',
	},
	hide: {
		display: 'none',
	},
	flexColumn: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	flexVariable: {
		overflow: 'hidden',
		flexGrow: 1,
	},
	stickyTable: {
		borderCollapse: 'separate',
		marginBottom: '1px',
	},
	fixedCell: {
		position: 'sticky',
		left: 0,
	},
	tableCell: {
		backgroundColor: 'white',
		borderRight: '1px solid #a7a7a7',
		borderBottom: '1px solid #a7a7a7',
		'th&': {
			borderTop: '1px solid #a7a7a7',
		},
		'&:nth-child(1)': {
			position: 'sticky',
			left: 0,
			zIndex: 2,
			borderLeft: '1px solid #a7a7a7',
		},
		// '&:nth-child(2)': {
		// 	position: 'sticky',
		// 	left: '34px',
		// 	zIndex: 2,
		// 	// fontWeight: 'bold',
		// 	whiteSpace: 'nowrap',
		// },
	},
	headerCell: {
		color: '#fff',
		backgroundColor: '#4c4f53',
		whiteSpace: 'nowrap',
		position: 'sticky',
		zIndex: 2,
		top: 0,
		'&:nth-child(-n+1)': {
			zIndex: 4
		},
		'& a:link, a:visited': {
			color: '#fff',
		},
	},
	iconCell: {
		width: '32px',
		minWidth: '32px',
		maxWidth: '32px',
		verticalAlign: 'middle',
	},
	rightCell: {
		textAlign: 'right',
	},
	imageCell: {
		padding: '4px',
		width: '72px',
		boxSizing: 'border-box',
		'& img': {
			display: 'block',
			margin: '0 auto',
		}
	},
	miniCell: {
		width: '1px', // minimum fit
	},
	folderCell: {
		backgroundColor: '#f1f1f1',
	},
	fileCell: {
		paddingLeft: '5em',
	},
	newline: {
		whiteSpace: 'break-spaces',
	},
	nowrap: {
		whiteSpace: 'nowrap',
	},
	alignRight: {
		textAlign: 'right',
	},
	pagination: {
		'& .MuiTablePagination-toolbar': {
			minHeight: 'auto',
		},
		'& .MuiIconButton-root': {
			padding: '6px',
		},
	},
	filterBox: {
		padding: '2px 4px',
		display: 'flex',
		alignItems: 'center',
		width: 400,
		marginBottom: 1,
		marginRight: 'auto',
	},
	disclaimerBox: {
		fontSize: '14px',
		whiteSpace: 'pre-wrap',
		marginRight: 5,
	},
	input: {
		marginLeft: 8,
		flex: 1,
		"& .MuiInputBase-input": {
			padding: '3px 0 4px',
		}
	},
	iconButton: {
		padding: 1,
	},
	closeButton: {
		padding: 2,
	},
	searchIcon: {
		color: 'rgba(0, 0, 0, 0.54)',
	},
	folderIcon: {
		fontSize: 20,
		color: '#695fcc',
		verticalAlign: 'bottom',
		marginRight: 4,
	},
	formInputText: {
		'& .MuiFormLabel-root': {
			color: 'black',
			'&.Mui-error': {
				color: '#f44336',
			}
		},
		'&:not(:first-child)': {
			marginTop: '20px',
		},
		'& .required': {
			color: '#f44336',
		},
		'& input[type=color]': {
			width: '50px',
			height: '30px',
		},
	},
	formInputParentFit: {
		width: '100%',
	},
	formInputLonLat: {
		maxWidth: '12em',
	},
	formRoot: {
		fontSize: '75%',
		'& .required': {
			color: '#f44336',
		},
		'& dl:not(:first-child)': {
			marginTop: '20px',
		},
		'& dt': {
			marginBottom: '5px',
		},
		'& #iconId-checkbox': {
			display: 'flex',
			minWidth: '10rem',
		},
	},
	formHorizontally: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'baseline',
	},
	formSpacer: {
		'& > *:not(:first-of-type)': { 
			marginLeft: '20px'
		},
	},
	fromHeading: {
		marginRight: '10px',
	},
	menuItem: {
		paddingTop: 0,
		paddingBottom: 0,
	},
	menuItemCheckbox: {
		padding: 4,
	},
	menuItemText: {
		marginLeft: 4,
	},
	chips: {
		display: 'flex',
		flexWrap: 'wrap',
	},
	chip: {
		margin: 2,
	},
	dragImageBox: {
		border: '3px dashed rgba(128, 128, 128, 0.5)',
		borderRadius: '2px',
		'& *': {
			cursor: 'pointer',
		}
	},
	dragImageBoxLabel: {
		fontSize: '75%',
		margin: '-6px 0 2px 14px',
	},
	dragImageBoxActive: {
		borderColor: 'rgba(80, 80, 255, 0.8)',
	},
	dragImageTextBox: {
		// width: '210px',
		width: '300px',
		height: '140px',
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		padding: '10px',
		fontSize: '15px',
		whiteSpace: 'break-spaces',
	},
	dragResultTextBox: {
		justifyContent: 'space-between',
		width: '100%',
		flexGrow: 1,
		display: 'flex',
	},
	dragResultDisclaimer: {
		fontSize: '12px',
	},
	ellipsis: {
		// overflow: 'hidden',
		// whiteSpace: 'nowrap',
		// textOverflow: 'ellipsis',
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-all',
		paddingRight: '1em',
	},
});

const columns = [
	{ id: 'name', label: 'Name' },
	{ id: 'size', label: 'Size', format: v => {
		return `${formatSize(v)}`;
	} },
	{ id: 'color', label: 'Color', format: v => {
		return (<Box style={{ width: '16px', height: '16px', backgroundColor: `#${v}`, border: '1px solid black' }} title={`#${v}`}></Box>);
	} },
];

const formInputList = [
	{
		code: 'file',
		name: 'File',
		type: 'file',
		rules: {
			required: true,
		},
		default: null,
	},
	{
		code: 'name',
		name: 'Name',
		type: 'text',
		rules: {
			required: true,
			pattern: /^[^'"!@#$%^&*();:{}[\]>? 　]*$/,
			maxLength: 40,
			maxByteLength: 40,
		},
		default: ""
	},
	{
		code: 'folder_name',
		name: 'Folder',
		type: 'text',
		data: {
			list: 'folderList',
			key: 'folder_name',
			name: 'folder_name',
		},
		rules: {
			required: true,
			pattern: /^[^'"!@#$%^&*();:{}[\]>? 　/<>|\\]*$/,
			maxLength: 40,
			maxByteLength: 40,
		},
		default: '',
	},
	{
		code: 'color',
		name: 'Color',
		type: 'color',
		rules: {
			required: true,
			pattern: /^#[0-9a-fA-F]{6}$/,
			maxLength: 7,
		},
		default: "#000000"
	},
];

const formDefault = formInputList.reduce((a, v) => {
	if ("default" in v) {
		a[v.code] = v.default;
	} else {
		a[v.code] = "";
	}
	return a;
}, {});

const formInputItem = code => {
	return formInputList.find(elem => elem.code === code) || {
		code,
		rules: {}
	};
};

const KIRO_BYTES = 1024;
const MEGA_BYTES = 1024 * 1024;
const formatSize = size => {
	if (size >= MEGA_BYTES) {
		return `${Math.round(size / MEGA_BYTES)} MB`;
	} else {
		return `${Math.round(size / KIRO_BYTES)} KB`;
	}
}

const PointAreaExternalFileListTable = memo(forwardRef(({filterWord, handleEdit, setWaiting, tableRef, setDisabled}, ref) => {
	const {
		customerExternalFileList,
		registerCustomerExternalFile,
		updateCustomerExternalFile,
		deleteCustomerExternalFile,
		refreshCustomerExternalFileList
	} = useCustomerExternalFileList();
	const [del, setDel] = useState();
	const [message, setMessage] = useState();
	const { showError, showWarning } = useAlert();

	const classes = useStyles();
	const {t} = useTranslation();

	const handleDelete = (row) => {
		const detail = [
			"\n",
			`${t('Name')}: ${row.name}`
		].join('\n');
	
		if (row.type !== 'folder') {
			setMessage(t('COMPANY_FILE_DELETE_CONFIRM', {detail}));
		} else {
			setMessage(t('COMPANY_FILE_FOLDER_DELETE_CONFIRM', {detail}));
		}
		setDel(row);
		setOpen(true);
	}

	const sortedCustomerPointList = useMemo(() => {
		return Array.from(customerExternalFileList).sort((a, b) => a.folder_name.localeCompare(b.folder_name) || a.name.localeCompare(b.name));
	}, [customerExternalFileList]);

	useEffect(() => {
		setDisabled(customerExternalFileList.length >= MAX_FILE_NUMBER);
	}, [customerExternalFileList, setDisabled]);

	const folderList = useMemo(() => {
		return customerExternalFileList
			.map(v => ({
					"folder_id": v.file_id,
					"folder_name": v.folder_name,
				}))
			.sort((a, b) => a.folder_name.localeCompare(b.folder_name))
			.reduce((a, v) => {
				if (a.length === 0 || a[a.length - 1].folder_name !== v.folder_name) {
					a.push(v);
				}
				return a;
			}, []);
	}, [customerExternalFileList]);

	const list = useMemo(() => {
		let filteredList;
		if (filterWord === "") {
			filteredList = sortedCustomerPointList;
		}
		let lastFolder = null;
		let files;
		filteredList = sortedCustomerPointList.filter(row => {
			return columns.some(col => {
				switch (col.id) {
					default:
						const cell = row[col.id] && ((col.format && col.format(row[col.id], t)) || row[col.id]);
						return cell !== undefined && cell !== null && String(cell).indexOf(filterWord) !== -1;
				}
			});
		});
		return filteredList.reduce((a, v) => {
			if (lastFolder !== v.folder_name) {
				files = [];
				a.push({
					file_id: `${v.file_id}-folder`,
					type: 'folder',
					folder_name: v.folder_name,
					name: v.folder_name,
					files: files,
				});
			}
			const file = {
				type: 'file',
				...v
			};
			a.push(file);
			files.push(file);
			lastFolder = v.folder_name;
			return a;
		}, []);
	}, [sortedCustomerPointList, filterWord, t]);

	useEffect(() => {
		return () => {
			refreshCustomerExternalFileList();
		}
	}, [refreshCustomerExternalFileList]);

	useImperativeHandle(ref, () => ({
		folderList,
		register: (registerData, fileData) => {
			setWaiting(true);
			registerCustomerExternalFile(registerData, fileData)
				.then(() => {
					// console.log("register comp");
				})
				.catch(e => {
					console.error("register error", e);
					if (e?.response?.status === 422) {
						showWarning(t('Invalid file format'));
					} else {
						showError(`${t('Register failed')}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		update: (updateData, fileData) => {
			setWaiting(true);
			updateCustomerExternalFile(updateData, fileData)
				.then(() => {
					// console.log("update comp");
				})
				.catch(e => {
					const name = t('Company Setting');
					if (e.response?.status === DATA_HAS_BEEN_CHANGED_ERROR) {
						console.warn("update failed", e);
						showWarning(`${t('validate_optimistic_lock_with_name', {name})}`, {timeout: 5000});
						refreshCustomerExternalFileList();
					} else {
						console.error("update error", e);
						showError(`${t('update_failed_with_name', {name})}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		delete: (deleteData) => {
			setWaiting(true);
			deleteCustomerExternalFile(deleteData)
				.then(() => {
					// console.info("delete comp");
				})
				.catch(e => {
					const name = t('Company Setting');
					if (e.response?.status === DATA_HAS_BEEN_CHANGED_ERROR) {
						console.warn("delete failed", e);
						showWarning(`${t('validate_optimistic_lock_with_name', {name})}`, {timeout: 5000});
						refreshCustomerExternalFileList();
					} else {
						console.error("delete error", e);
						showError(`${t('delete_failed_with_name', {name})}`);
					}
				})
				.finally(() => {
					setWaiting(false);
				});
		},
		check: (item, value) => {
			return customerExternalFileList.every(v => v[item] !== value);
		},
		totalSize: customerExternalFileList.reduce((a, v) => a + v.size, 0),
	}));

	// 確認ダイアログ
	const [open, setOpen] = useState(false);

	const handleDeleteOk = () =>{
		let _data;
		if (del.type !== 'folder') {
			_data = [{
				file_id: del.file_id,
				update_time: del.update_time,
				update_by_user: del.update_by_user,
			}];
		} else {
			_data = del.files.map(v => {
				return {
					file_id: v.file_id,
					update_time: v.update_time,
					update_by_user: v.update_by_user,
				};
			})
		}
		setOpen(false);
		ref.current.delete(_data);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleKeyUp = e => {
		if (e.keyCode === 13) {
			handleDeleteOk();
		}
	};

	return (
		<>
			<Box className={classes.flexColumn}>
				<Box className={classes.flexVariable}>
					<TableContainer className={classes.container} ref={tableRef}>
						<Table aria-label="sticky table" size="small" className={classes.stickyTable}>
							<TableHead>
								<TableRow className={classes.tableRow}>
									<TableCell
										key='edit'
										className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
										padding="none"
									>
									</TableCell>
									{columns.map((column, _i) => (
										column.label && <TableCell
											key={column.id}
											align={column.align}
											style={{ minWidth: 0 }}
											className={clsx(
												classes.headerCell,
												classes.tableCell,
												['folder_name', 'color', 'size'].includes(column.id) && classes.miniCell
											)}
											{...column.colSpan ? {colSpan: column.colSpan} : ''}
										>
											{t(column.label)}
										</TableCell>
									))}
									<TableCell
										key='delete'
										className={clsx(classes.headerCell, classes.tableCell, classes.iconCell)}
										padding="none"
									>
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{list.map((row) => {
									return (
										<TableRow hover role="checkbox" tabIndex={-1} key={row.file_id}>
											<TableCell key='editBtn' className={clsx(classes.tableCell, classes.iconCell, row.type === 'folder' && classes.folderCell)} padding="none" align="center">
												<RoleControl subject={SUBJ_POINT_AREA_SETTING_FILE} verb={VERB_UPDATE} >
													<IconButton aria-label="edit" size="small" className={classes.smallBtn} onClick={() => handleEdit(row)}>
														<Tooltip title="edit" arrow >
															<PencilIcon fontSize="small" />
														</Tooltip>
													</IconButton>
												</RoleControl>
											</TableCell>
											{columns.map((column, _i) => {
												const value = row[column.id];
												return (
													(value !== undefined) && (
														<TableCell
															key={column.id}
															align={column.align}
															className={clsx(
																classes.tableCell, 
																classes.newline,
																row.type === 'folder' && classes.folderCell,
																row.type === 'file' && column.id === 'name' && classes.fileCell,
																column.id === 'size' && classes.rightCell,
																column.id === 'size' && classes.nowrap,
															)}
															{...row.type === 'folder' && column.id === 'name' ? {colSpan: 3} : ''}
														>
															{
																row.type === 'folder' && column.id === 'name' && <FolderIcon className={clsx(classes.folderIcon)} viewBox="0 0 24 24" alt="Describe"/>
															}
															{
																['lat', 'lon'].includes(column.id) ?
																	column.format(row['position']) :
																column.id === 'iconName' ?
																	row['iconId'] <= 100 ? t(POINT_DEFAULT_ICONS.find(v => v.iconId === row['iconId'])?.name) : value :
																	column.format ? column.format(value, t) : value
															}
														</TableCell>
													)
												);
											})}
											<TableCell key='deleteBtn' className={clsx(classes.tableCell, classes.iconCell)} padding="none" align="center">
												<RoleControl subject={SUBJ_POINT_AREA_SETTING_FILE} verb={VERB_DELETE} >
													<IconButton aria-label="delete" size="small" className={classes.smallBtn} onClick={() => handleDelete(row)}>
														<Tooltip title="delete" arrow >
															<DeleteIcon fontSize="small" />
														</Tooltip>
													</IconButton>
												</RoleControl>
											</TableCell>
										</TableRow>
									);
								})}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			</Box>
			<Dialog open={open} onClose={handleClose} onKeyUp={handleKeyUp}>
				<DialogContent>
					<DialogContentText className={classes.newline}>{message}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} color="primary">Cancel</Button>
					<Button onClick={handleDeleteOk} color="primary">Ok</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}));

function PointAreaExternalFileList() {
	const tableRef = useRef(null);
	const childRef = useRef({});
	const inputFilterRef = useRef(null);
	const [formData, setFormData] = useState({});
	const [filterWord, setFileterWord] = useState('');
	const [open, setOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);
	const [folderMode, setFolderMode] = useState(false);
	const [waiting, setWaiting] = useState(false);
	const [manual, setManual] = useState(false);
	const [disabled, setDisabled] = useState(false);

	const inputRefs = formInputList.reduce((a, v) => (a[v.code] = createRef()) && a, {});

	const { control, formState: { errors, isDirty, isValid }, setValue, getValues, reset } = useForm({
		mode: 'onChange',
	});

	const classes = useStyles();
	const {t} = useTranslation();

	const cancelDrop = event => {
		event.preventDefault();
		return false;
	}

	useEffect(() => {
		document.addEventListener('dragover', cancelDrop, false);
		document.addEventListener('drop', cancelDrop, false);
		return () => {
			document.removeEventListener('dragover', cancelDrop, false);
			document.removeEventListener('drop', cancelDrop, false);
			setFileterWord('');
		}
	}, [setFileterWord]);

	const handleNew = () => {
		const _formDefault = {
			...formDefault,
		}
		setFolderMode(false);
		setEditMode(false);
		setManual(false);
		reset(_formDefault);
		setFormData({});
		setOpen(true);
	}

	const handleEdit = useCallback(row => {
		setFolderMode(row.type === 'folder');
		setEditMode(true);
		setManual(false);
		const _formData = Object.assign({}, row, {
			file: NO_CHANGE_FILE_SENTINEL,
		});

		//色
		_formData.color = `#${_formData.color}`;

		Object.keys(formDefault).forEach(key => (_formData[key] === null || _formData[key] === undefined) && (_formData[key] = formDefault[key]));
		reset(_formData);
		setFormData(_formData);
		setOpen(true);
	}, [reset]);

	const handleSearch = () => {
		const word = inputFilterRef.current.value;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	const handleDeleteFilter = () => {
		const word = "";
		inputFilterRef.current.value = word;
		setFileterWord(word);
		tableRef.current.scrollTop = 0;
	}

	const handleClose = () => {
		setOpen(false);
	}

	const handleClick = () => {
		setValue('folder_name', '', {shouldDirty: true, shouldValidate: true});
		setManual(!manual);
	}

	const handleSave = () => {
		const data = getValues();
		const common = [
			'folder_name',
			'file_name',
			'object_key',
			'name',
			'size',
		];

		let _data;
		if (data.type !== 'folder') {
			const _d = {};

			_d.color = data.color.slice(1);

			common.forEach(v => {
				_d[v] = data[v]
			});

			if (!editMode) {
				_data = _d;
			} else {
				if (_d.folder_name !== formData.folder_name) {
					const objectKey = _d.object_key.split('/');
					objectKey[1] = _d.folder_name;
					_d.object_key = objectKey.join('/');
				}
				_d.file_id = formData.file_id;
				_d.update_time = formData.update_time;
				_d.update_by_user = formData.update_by_user;
				_data = [_d];
			}

		} else {
			_data = data.files.map(v => {
				const _v = Object.assign({}, v);
				if (_v.folder_name !== data.folder_name) {
					const objectKey = _v.object_key.split('/');
					objectKey[1] = data.folder_name;
					_v.object_key = objectKey.join('/');
				}
				_v.folder_name = data.folder_name;
				return _v;
			});
		}

		if (!editMode) {
			childRef.current.register(_data, data.file);
		} else {
			let file = null;
			if (data.file && data.file !== NO_CHANGE_FILE_SENTINEL) {
				file = data.file;
			}
			childRef.current.update(_data, file);
		}
		setOpen(false);
	}

	return (
		<Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
			<Box style={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 50px 10px 10px' }}>
				<Paper component="form" className={classes.filterBox} onSubmit={(event) => { handleSearch(); event.preventDefault(); }}>
					<div className={classes.searchIcon}>
						<SearchIcon />
					</div>
					<InputBase
						className={classes.input}
						placeholder={`${t('Name')}`}
						inputProps={{ 'aria-label': 'filter aircraft group list' }}
						inputRef={inputFilterRef}
					/>
					{filterWord.length > 0 && <IconButton type="button" className={classes.iconButton} aria-label="delete" onClick={handleDeleteFilter}>
						<CloseIcon />
					</IconButton>}
				</Paper>
				<Box className={classes.disclaimerBox}>
					{t('FILE_LIMIT_DESCRIBE', { number: MAX_FILE_NUMBER, size: formatSize(MAX_TOTAL_FILE_SIZE) })}
				</Box>
				<RoleControl subject={SUBJ_POINT_AREA_SETTING_FILE} verb={VERB_CREATE}>
					<Button variant="outlined" size="small" className={classes.btn} disabled={disabled} onClick={handleNew}>{t('New')}</Button>
				</RoleControl>
			</Box>
			<Box style={{ padding: '0 10px 10px', flexGrow: 1, overflow: 'hidden' }}>
				{waiting && <Loading backdrop={true} />}
				<Suspense fallback={<Loading />}>
					<PointAreaExternalFileListTable filterWord={filterWord} ref={childRef} handleEdit={handleEdit} setWaiting={setWaiting} tableRef={tableRef} setDisabled={setDisabled} />
				</Suspense>
			</Box>
			<Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open}>
				<DialogContent>
					<Box style={{ display: 'flex', justifyContent: 'flex-end', flexDirection: 'row-reverse' }}>
						<Box style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'column', marginLeft: '15px' }}>
							<Box style={{ marginLeft: 'auto'}}>
								<IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
									<CloseIcon />
								</IconButton>
							</Box>
							<Box>
								<Button variant="outlined" size="small" className={classes.btn} onClick={handleSave} disabled={!(isDirty && isValid)}>{t('save')}</Button>
							</Box>
						</Box>
						<Box style={{ display: 'flex', flexDirection: 'column', paddingBottom: '40px' }}>
							<ul className={classes.formRoot}>
								{!folderMode ?
									<li>
										<dl className={classes.dataList}>
											<dt className={classes.dataLabel}>{t('File')}<span className="required"> * {t('required')}</span></dt>
											<dd><FormFileSelect code="file" {...{inputRefs, childRef, control, setValue, getValues, formData, errors, editMode}} /></dd>
										</dl>
										<dl className={classes.dataList}>
											<dt className={classes.dataLabel}>{t('Name')}<span className="required"> * {t('required')}</span></dt>
											<dd><FormText code="name" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} /></dd>
										</dl>
										<dl className={classes.dataList}>
											<dt className={classes.dataLabel}>{t('Folder')}<span className="required"> * {t('required')}</span></dt>
											<dd style={{ position: 'relative' }} className={classes.formSpacer}>
												{!manual ?
													<>
														<FormSelect code="folder_name" {...{ inputRefs, childRef, control, getValues, formData, errors, editMode }} />
														<div style={{ position: 'absolute', right: -30, top: 6 }}>
															<IconButton aria-label="add" style={{ padding: '4px' }} onClick={handleClick}>
																<AddCircleIcon fontSize="small" />
															</IconButton>
														</div>
													</>
													:
													<>
														<FormText code="folder_name" {...{ inputRefs, childRef, control, getValues, formData, errors, editMode }} />
														<div style={{ position: 'absolute', right: -30, top: 6 }}>
															<IconButton aria-label="remove" style={{ padding: '4px' }} onClick={handleClick}>
																<CancelIcon fontSize="small" />
															</IconButton>
														</div>
													</>
												}
											</dd>
										</dl>
										<dl className={classes.dataList}>
											<dt className={classes.dataLabel}>{t('Color')}<span className="required"> * {t('required')}</span></dt>
											<dd><FormText code="color" {...{inputRefs, childRef, control, getValues, formData, errors, editMode}} /></dd>
										</dl>
									</li>
								:
									<li style={{ minWidth: 300 }}>
										<dl className={classes.dataList}>
											<dt className={classes.dataLabel}>{t('Folder')}<span className="required"> * {t('required')}</span></dt>
											<dd style={{ position: 'relative' }} className={classes.formSpacer}>
												{!manual ?
													<>
														<FormSelect code="folder_name" {...{ inputRefs, childRef, control, getValues, formData, errors, editMode }} />
														<div style={{ position: 'absolute', right: -30, top: 6 }}>
															<IconButton aria-label="add" style={{ padding: '4px' }} onClick={handleClick}>
																<AddCircleIcon fontSize="small" />
															</IconButton>
														</div>
													</>
													:
													<>
														<FormText code="folder_name" {...{ inputRefs, childRef, control, getValues, formData, errors, editMode }} />
														<div style={{ position: 'absolute', right: -30, top: 6 }}>
															<IconButton aria-label="remove" style={{ padding: '4px' }} onClick={handleClick}>
																<CancelIcon fontSize="small" />
															</IconButton>
														</div>
													</>
												}
											</dd>
										</dl>
									</li>
								}
							</ul>
						</Box>
					</Box>
				</DialogContent>
			</Dialog>
		</Box>
	)
}

export default PointAreaExternalFileList;

const makeRules = (item, t, {childRef, getValues, formData}) => {
	const result = {
		required: item.rules.required ? t('validate_required') : false,
	};
	if (item.rules.pattern) {
		result.pattern = {
			value: item.rules.pattern,
			message: t('validate_pattern')
		};
	}
	if (item.rules.maxLength) {
		result.maxLength = {
			value: item.rules.maxLength,
			message: t('validate_max_length', { maxLength: item.rules.maxLength }),
		};
	}
	result.validate = {
		common: value => {
			if (item.rules.maxByteLength) {
				if(FPEUtils.strSJISBytes(value) > item.rules.maxByteLength) {
					return t('Too many characters');
				}
			}
			if (!item.rules.unique) {
				return true;
			}
			if (formData[item.code] === value) {
				return true;
			}
			return childRef.current.check(item.code, value) || t('already exists')
		},
		...Object.fromEntries(Object.entries({...item.rules.validate}).map(([key, func]) => {
			return [key, (value) => func(value, {getValues, t})];
		})),
	};

	return result;
};

const FormText = ({code, name, inputRefs, childRef, control, getValues, formData, errors, editMode, ...prop}) => {

	const classes = useStyles();
	const {t} = useTranslation();

	const item = formInputItem(code);
	const rules = makeRules(item, t, {childRef, getValues, formData});
	const inputProps = {};
	if (item.type !== 'text') {
		inputProps.type = item.type;
	} else if (item.unit) {
		inputProps.endAdornment = <InputAdornment position="start">{item.unit}</InputAdornment>
	}

	return (
		<Controller
			key={name || item.code}
			control={control}
			rules={rules}
			render={({ field: { name, onChange, onBlur, value } }) => (
				<TextField
					className={clsx(classes.formInputText, item.type !== "color" && classes.formInputParentFit)}
					// label={<div>{item.name}{item.rules.required && <span className="required"> * {t('required')}</span>}</div>}
					name={name}
					id={name}
					variant={item.type !== "color" ? "outlined" : "standard"}
					size="small"
					InputLabelProps={{
						shrink: true,
					}}
					error={!!errors[name]}
					inputRef={inputRefs[name]}
					InputProps={inputProps}
					helperText={t(errors[name]?.message)}
					onChange={onChange}
					onBlur={onBlur}
					value={value}
					disabled={editMode && item.rules.reedit === false}
					{...prop}
				>
				</TextField>
			)}
			name={name || item.code}
		/>
	);
};

const FormSelect = ({code, inputRefs, childRef, control, getValues, formData, errors, editMode, ...prop}) => {

	const classes = useStyles();
	const {t} = useTranslation();

	const item = formInputItem(code);
	const rules = makeRules(item, t, {childRef, getValues, formData});

	return (
		<Controller
			name={item.code}
			control={control}
			rules={rules}
			render={({ field: { name, onChange, onBlur, value } }) => (
				<TextField
					className={clsx(classes.formInputText, classes.formInputParentFit)}
					name={name}
					id={name}
					select
					variant="outlined"
					size="small"
					InputLabelProps={{
						shrink: true,
					}}
					error={!!errors[name]}
					inputRef={inputRefs[name]}
					helperText={t(errors[name]?.message)}
					onChange={onChange}
					onBlur={onBlur}
					value={value}
					disabled={editMode && item.rules.reedit === false}
					{...prop}
				>
					{((Array.isArray(item.data.list) && item.data.list) || (childRef.current[item.data.list] && childRef.current[item.data.list]) || []).map((v) => (
						<MenuItem key={v[item.data.key]} value={v[item.data.key]}>
							{v[item.data.name]}
						</MenuItem>
					))}
				</TextField>
			)}
		/>
	);
};

const checkGeoJSON = (buffer) => {
	try {
		const decoder = new TextDecoder('utf-8', { fatal: true, ignoreBOM: false });
		const text = decoder.decode(buffer);
		const geojson = JSON.parse(text);
		return JSON.stringify(geojson);
	} catch (e) {
		return false;
	}
}

const FormFileSelect = ({code, inputRefs, childRef, control, setValue, getValues, formData, errors, editMode, ...prop}) => {
	const [onTarget, setOnTarget] = useState(false);
	const [preview, setPreview] = useState(() => {
		const [fileName, size] = getValues(['file_name', 'size']);
		if (fileName !== undefined && size !== undefined) {
			return {
				name: fileName,
				size: size,
			};
		}
	});
	const { showError } = useAlert();

	const classes = useStyles();
	const {t} = useTranslation();

	const item = formInputItem(code);
	const rules = makeRules(item, t, {childRef, getValues, formData});

	const changePreview = file => {
		if (file) {
			if (file.size > MAX_FILE_SIZE) {
				showError(t('File size is too large'), { position: alertPosition.BOTTOM_CENTER, timeout: 5000 });
				return;
			}
			// if (!SUPPORT_IMAGE_TYPE.includes(file.type)) {
			// 	showError(t('Unsupported file type'), { position: alertPosition.BOTTOM_CENTER, timeout: 5000 });
			// 	return;
			// }
			const fileReader = new FileReader();
			fileReader.onload = (event) => {
				const geojsonText = checkGeoJSON(event.target.result);
				if (geojsonText) {
					const size = geojsonText.length;
					if (childRef.current.totalSize + size > MAX_TOTAL_FILE_SIZE) {
						showError(t('Total file size has exceeded {{size}}.', {size: formatSize(MAX_TOTAL_FILE_SIZE)}), { position: alertPosition.BOTTOM_CENTER, timeout: 5000 });
						return;
					}
					setPreview({
						name: file.name,
						size: size,
					});
					if (getValues('name') === "") {
						setValue('name', file.name, {shouldDirty: true, shouldValidate: true});
					}
					setValue('file_name', file.name);
					setValue('size', size);
					setValue('file', geojsonText, {shouldDirty: true, shouldValidate: true});
				} else {
					showError(t('Invalid file format'), { position: alertPosition.BOTTOM_CENTER, timeout: 5000 });
					return;
				}
			}
			fileReader.readAsArrayBuffer(file);
		} else {
			setPreview();
		}
	}

	const handleImageChange = event => {
		const files = event.target.files;
		if (files.length >= 1) {
			changePreview(files[0]);
		}
	}

	const handleDragOver = event => {
		event.preventDefault();
		setOnTarget(true);
	}

	const handleDragLeave = event => {
		event.preventDefault();
		setOnTarget(false);
	}

	const handleDrop = event => {
		event.preventDefault();
		setOnTarget(false);
		const files = event.dataTransfer.files;
		if (files.length >= 1) {
			changePreview(files[0]);
		}
	}

	return (
		<Controller
			name={item.code}
			control={control}
			rules={rules}
			render={({ field: { name } }) => (
				<Box
					className={clsx(classes.dragImageBox, onTarget && classes.dragImageBoxActive)}
					onDrop={handleDrop}
					onDragOver={handleDragOver}
					onDragLeave={handleDragLeave}
				>
					<label htmlFor="upload-file" style={{ display: 'flex', flexDirection: 'column' }}>
						{!preview ?
							<pre className={classes.dragImageTextBox}>
								{t('DROP_FILE_DESCRIBE', { 'size': formatSize(MAX_FILE_SIZE) })}
							</pre> :
							<div className={classes.dragImageTextBox} style={{ flexDirection: 'column' }}>
								<div className={clsx(classes.dragResultTextBox)}>
									<div className={classes.ellipsis}>{preview.name}</div>
									<div className={classes.nowrap}>{formatSize(preview.size)}</div>
								</div>
								<div className={classes.dragResultDisclaimer}>
									{t('DROP_FILE_RESULT_DESCRIBE')}
								</div>
							</div>
						}
						<input
							accept=".json,.geojson"
							className={classes.input}
							id="upload-file"
							name={name}
							multiple
							type="file"
							onChange={handleImageChange}
							style={{ display: 'none' }}
						/>
					</label>
				</Box>
			)}
		/>
	);
}
